import { useIntl } from 'react-intl';
import { useState, useEffect } from 'react';
import { Space } from 'antd';
import { SorterResult, TableRowSelection } from 'antd/lib/table/interface';

import {
  ListStockItemsQuery,
  StockItem,
  StockItemType,
  StockOrderColumnConfig,
} from '@/core/domain/stock/StockItems/StockItems.types';
import { getRowKey } from '@/core/domain/stock/StockItems/StockItems.utils';
import { useStockItems } from '@/core/domain/stock/StockItems/StockItems.hooks';

import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';

import { AppPageHeader } from '@/components/_common';
import { PartItemFieldOption } from '@/core/domain/stock/Configuration/Configuration.types';
import { COLUMNS_WIDTH, tableSorterOrder } from '@/utils/table';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { RadioSelect } from '../../CreateQuotationItemModal';
import StatusTag from '@/components/_common/StatusTag';
import { useDebounce } from '@/hooks';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import QuotationPartsExtraFilters from '../QuotationPartsExtraFilters';

interface StockItemsTableProps {
  type: RadioSelect;
  companiesIds?: number[];
  rowSelection?: TableRowSelection<StockItem>;
  enabled: boolean;
}

function PartsOrConsumablesList({
  type,
  companiesIds,
  rowSelection,
  enabled = true,
}: StockItemsTableProps) {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<ListStockItemsQuery>({
    companiesIds,
    order: 'ASC',
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, hasNextPage, fetchNextPage } = useStockItems(
    enabled,
    queryDebounce,
  );

  const stockItems = flatInfiniteResult(data);

  useEffect(() => {
    setQueryParams({});
    if (type) {
      if (type !== RadioSelect.StockBellow && type !== RadioSelect.OS) {
        setQueryParams((prev) => ({
          ...prev,
          itemType:
            type === RadioSelect.Parts
              ? StockItemType.Part
              : StockItemType.Consumable,
          active: true,
          companiesIds,
        }));
      } else {
        setQueryParams((prev) => ({
          ...prev,
          itemType: StockItemType.Part,
          below: true,
          active: true,
          companiesIds,
        }));
      }
    }
  }, [type]);

  function handleOrder(sorter: SorterResult<StockItem>) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          if (prev.orderColumns === StockOrderColumnConfig[sorter.columnKey]) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            orderColumns: StockOrderColumnConfig[sorter.columnKey],
          };
        }

        return prev;
      });
    }
  }

  const columns: ColumnsType<StockItem> = [
    useConfigurationColumnsPart({ internalCode: 'sku' }),
    {
      key: PartItemFieldOption.Description,
      title: intl.formatMessage({
        id: `settings.partsConfiguration.${PartItemFieldOption.Description}`,
      }),
      dataIndex: 'description',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      render: (value: string) => value || '-',
      sorter: true,
    },
  ];

  const tableColumns: ColumnsType<StockItem> = [
    ...columns,
    {
      ellipsis: true,
      width: 120,
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.type',
      }),
      render(row: StockItem) {
        let label = '-';

        if (row.partId) {
          label = intl.formatMessage({
            id: 'stockCorrections.domain.items.type.item_type.part',
          });
        } else if (row.consumableId) {
          label = intl.formatMessage({
            id: 'stockCorrections.domain.items.type.item_type.consumable',
          });
        }

        return <StatusTag label={label} color="blue" />;
      },
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <AppPageHeader
        onSearch={(value: string) => setQueryParams({ search: value })}
        extraFilters={
          <QuotationPartsExtraFilters setQueryParams={setQueryParams} />
        }
      />

      <InfiniteTable<StockItem>
        columns={tableColumns}
        dataSource={stockItems}
        rowKey={(row) => getRowKey(row)}
        loading={isFetching}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        rowSelection={rowSelection}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter)) {
            handleOrder(sorter);
          }
        }}
        scroll={{
          x: tableColumns.reduce((prev, curr) => {
            const w = curr.width as number;

            return prev + w;
          }, 0),
        }}
      />
    </Space>
  );
}

export default PartsOrConsumablesList;
